<template>
  <section class="mesba-access">
    <div class="mesba-access__header">
      <div>
        <div class="overline primary--text">MESBA CONNECTION</div>
        <h2 class="mesba-access__title">Share organization data securely</h2>
        <p class="mesba-access__subtitle">
          Create a connection key for Mesba and choose exactly which read-only
          information it may receive. You can change access or revoke the key at any time.
        </p>
      </div>
      <v-btn color="primary" depressed @click="openCreate">
        <v-icon left>mdi-key-plus</v-icon>
        Generate key
      </v-btn>
    </div>

    <v-alert
      v-if="errorMessage"
      type="error"
      outlined
      class="mt-5"
    >
      {{ errorMessage }}
      <v-btn text small color="error" class="ml-2" @click="loadKeys">Try again</v-btn>
    </v-alert>

    <div v-if="loading" class="py-8 text-center">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <v-card
      v-else-if="!keys.length"
      outlined
      class="mesba-access__empty mt-5"
    >
      <v-icon size="42" color="primary">mdi-shield-key-outline</v-icon>
      <h3>No connection keys yet</h3>
      <p>
        Generate a key, choose the information to share, and give the key
        directly to your Mesba administrator.
      </p>
      <v-btn color="primary" outlined @click="openCreate">Generate first key</v-btn>
    </v-card>

    <div v-else class="mesba-access__list mt-5">
      <v-card
        v-for="keyItem in keys"
        :key="keyItem.id"
        outlined
        class="mesba-access__key"
      >
        <div class="d-flex align-start">
          <v-avatar color="#E8F4EF" size="46">
            <v-icon color="primary">mdi-key-chain-variant</v-icon>
          </v-avatar>
          <div class="ml-4 flex-grow-1">
            <div class="d-flex align-center flex-wrap">
              <strong class="mr-2">{{ keyItem.name }}</strong>
              <v-chip
                x-small
                :color="keyItem.status === 'active' ? '#E7F5EF' : '#F2F2F2'"
                :text-color="keyItem.status === 'active' ? '#176B55' : '#6B7280'"
              >
                {{ keyItem.status === "active" ? "Active" : "Revoked" }}
              </v-chip>
            </div>
            <div class="caption grey--text text--darken-1 mt-1">
              Key ending reference {{ keyItem.tokenPrefix }}…
              <span v-if="keyItem.lastUsedAt">
                · Last used {{ formatDate(keyItem.lastUsedAt) }}
              </span>
              <span v-else> · Not connected yet</span>
            </div>
            <div v-if="keyItem.token" class="d-flex align-center mt-3 key-value">
              <v-text-field
                :value="keyItem.token"
                :type="visibleKeys[keyItem.id] ? 'text' : 'password'"
                readonly
                outlined
                dense
                hide-details
                label="Connection key"
                :append-icon="visibleKeys[keyItem.id] ? 'mdi-eye-off' : 'mdi-eye'"
                @click:append="toggleKeyVisibility(keyItem.id)"
              />
              <v-btn icon color="primary" class="ml-2" aria-label="Copy connection key" @click="copySavedToken(keyItem.token)">
                <v-icon>mdi-content-copy</v-icon>
              </v-btn>
            </div>
            <v-alert v-else dense outlined type="info" class="mt-3 mb-0">
              This key was created before reusable viewing was enabled. Generate a new key to make it viewable here.
            </v-alert>
            <div class="scope-list mt-3">
              <v-chip
                v-for="scope in activeScopeLabels(keyItem.scopes)"
                :key="scope"
                x-small
                color="#F1F5F3"
              >
                {{ scope }}
              </v-chip>
            </div>
          </div>
          <div v-if="keyItem.status === 'active'" class="key-actions">
            <v-btn text small color="primary" @click="openEdit(keyItem)">
              Edit access
            </v-btn>
            <v-btn text small color="error" @click="confirmRevoke(keyItem)">
              Revoke
            </v-btn>
          </div>
        </div>
      </v-card>
    </div>

    <v-dialog v-model="formDialog" persistent max-width="720">
      <v-card>
        <v-card-title class="dialog-heading">
          <div>
            <div>{{ editingKey ? "Edit Mesba access" : "Generate Mesba key" }}</div>
            <small>
              The connection is always read-only. Select the data Mesba may receive.
            </small>
          </div>
          <v-spacer />
          <v-btn icon aria-label="Close" @click="closeForm">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-6">
          <v-text-field
            v-model.trim="form.name"
            outlined
            label="Key name"
            placeholder="Example: Mesba annual testing"
          />

          <div class="permission-heading">Directory information</div>
          <div class="permission-subtitle">
            Organization identity is always included so Mesba can label the shared records.
          </div>
          <div class="permission-grid mt-3">
            <div
              v-for="permission in standardPermissions"
              :key="permission.value"
              class="permission-row"
            >
              <div>
                <strong>{{ permission.label }}</strong>
                <small>{{ permission.description }}</small>
              </div>
              <v-switch
                v-model="form.scopes[permission.value]"
                color="primary"
                hide-details
                :disabled="permission.required"
                :aria-label="permission.label"
              />
            </div>
          </div>

          <v-alert color="#FFF7E6" icon="mdi-shield-alert-outline" class="mt-6 mb-3">
            <strong>Sensitive information</strong>
            <div class="caption mt-1">
              Keep these off unless Mesba has a clear need for the information.
            </div>
          </v-alert>
          <div class="permission-grid">
            <div
              v-for="permission in sensitivePermissions"
              :key="permission.value"
              class="permission-row permission-row--sensitive"
            >
              <div>
                <strong>{{ permission.label }}</strong>
                <small>{{ permission.description }}</small>
              </div>
              <v-switch
                v-model="form.scopes[permission.value]"
                color="warning"
                hide-details
                :aria-label="permission.label"
              />
            </div>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn text @click="closeForm">Cancel</v-btn>
          <v-btn
            color="primary"
            depressed
            :loading="saving"
            :disabled="!form.name"
            @click="saveKey"
          >
            {{ editingKey ? "Save access" : "Generate key" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="tokenDialog" persistent max-width="650">
      <v-card>
        <v-card-title>Connection key created</v-card-title>
        <v-card-text>
          <v-alert type="warning" outlined>
            Copy this key now. For security, Taleem will not show it again.
          </v-alert>
          <v-textarea
            ref="tokenField"
            :value="newToken"
            readonly
            outlined
            rows="3"
            label="Mesba connection key"
            class="token-field"
          />
          <div class="caption grey--text text--darken-1">
            Send it through a trusted channel. Mesba will use it once to establish
            the read-only connection.
          </div>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-btn color="primary" outlined @click="copyToken">
            <v-icon left>mdi-content-copy</v-icon>
            {{ copied ? "Copied" : "Copy key" }}
          </v-btn>
          <v-spacer />
          <v-btn color="primary" depressed @click="closeToken">Done</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import settingService from "../service";

const defaultScopes = () => ({
  organization: true,
  departments: true,
  classrooms: true,
  students: true,
  teachers: true,
  guardians: true,
  attendance: false,
  studentDateOfBirth: false,
  guardianContact: false,
});

export default {
  name: "mesba-access",
  data() {
    return {
      loading: true,
      saving: false,
      keys: [],
      errorMessage: "",
      formDialog: false,
      tokenDialog: false,
      editingKey: null,
      newToken: "",
      copied: false,
      visibleKeys: {},
      form: {
        name: "",
        scopes: defaultScopes(),
      },
      standardPermissions: [
        { value: "organization", label: "Organization", description: "Name and basic organization identity.", required: true },
        { value: "departments", label: "Departments", description: "Department names and structure." },
        { value: "classrooms", label: "Classrooms", description: "Classroom names and organization placement." },
        { value: "students", label: "Students", description: "Student names and directory records." },
        { value: "teachers", label: "Teachers", description: "Teacher names and classroom relationships." },
        { value: "guardians", label: "Guardians", description: "Guardian names and student relationships." },
        { value: "attendance", label: "Attendance", description: "Student attendance records." },
      ],
      sensitivePermissions: [
        { value: "studentDateOfBirth", label: "Student dates of birth", description: "Share full student birth dates." },
        { value: "guardianContact", label: "Guardian contact details", description: "Share guardian phone numbers and email addresses." },
      ],
    };
  },
  created() {
    this.loadKeys();
  },
  methods: {
    async loadKeys() {
      this.loading = true;
      this.errorMessage = "";
      try {
        const response = await settingService.getMesbaAccessKeys();
        this.keys = response.data || [];
      } catch (error) {
        this.errorMessage =
          (error && error.data && error.data.message) ||
          (error && error.message) ||
          "Unable to load Mesba access keys.";
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.editingKey = null;
      this.form = { name: "Mesba connection", scopes: defaultScopes() };
      this.formDialog = true;
    },
    openEdit(keyItem) {
      this.editingKey = keyItem;
      this.form = {
        name: keyItem.name,
        scopes: { ...defaultScopes(), ...keyItem.scopes },
      };
      this.formDialog = true;
    },
    closeForm() {
      if (this.saving) return;
      this.formDialog = false;
    },
    async saveKey() {
      this.saving = true;
      try {
        if (this.editingKey) {
          await settingService.updateMesbaAccessKey(this.editingKey.id, this.form);
          this.formDialog = false;
          await this.loadKeys();
          this.showToast("Mesba access updated");
        } else {
          const response = await settingService.createMesbaAccessKey(this.form);
          this.newToken = response.data.token;
          this.formDialog = false;
          this.tokenDialog = true;
          await this.loadKeys();
        }
      } catch (error) {
        this.$swal(
          "Unable to save access",
          (error && error.data && error.data.message) ||
            (error && error.message) ||
            "Please try again.",
          "error"
        );
      } finally {
        this.saving = false;
      }
    },
    async confirmRevoke(keyItem) {
      const result = await this.$swal({
        title: "Revoke this connection key?",
        text: "Mesba will stop receiving new Taleem data immediately. Previously synced Mesba records will remain.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Revoke key",
        confirmButtonColor: "#C62828",
      });
      if (!result.value) return;
      try {
        await settingService.revokeMesbaAccessKey(keyItem.id);
        await this.loadKeys();
        this.showToast("Mesba access revoked");
      } catch (error) {
        this.$swal(
          "Unable to revoke key",
          (error && error.data && error.data.message) ||
            (error && error.message) ||
            "Please try again.",
          "error"
        );
      }
    },
    async copyToken() {
      try {
        await navigator.clipboard.writeText(this.newToken);
        this.copied = true;
      } catch (error) {
        const input = this.$refs.tokenField.$el.querySelector("textarea");
        input.select();
        document.execCommand("copy");
        this.copied = true;
      }
    },
    toggleKeyVisibility(id) {
      this.$set(this.visibleKeys, id, !this.visibleKeys[id]);
    },
    async copySavedToken(token) {
      try {
        await navigator.clipboard.writeText(token);
        this.showToast("Connection key copied");
      } catch (error) {
        this.$swal("Unable to copy key", "Select the key and copy it manually.", "error");
      }
    },
    closeToken() {
      this.tokenDialog = false;
      this.newToken = "";
      this.copied = false;
    },
    showToast(title) {
      this.$swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2500,
      }).fire({ icon: "success", title });
    },
    activeScopeLabels(scopes) {
      const all = [...this.standardPermissions, ...this.sensitivePermissions];
      return all.filter(item => scopes && scopes[item.value]).map(item => item.label);
    },
    formatDate(value) {
      return new Intl.DateTimeFormat(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(new Date(value));
    },
  },
};
</script>

<style scoped>
.mesba-access { padding: 24px; border-bottom: 1px solid #e5e7eb; }
.mesba-access__header { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; }
.mesba-access__title { color: #173d32; font-size: 22px; line-height: 1.3; margin: 2px 0 6px; }
.mesba-access__subtitle { color: #64746e; margin: 0; max-width: 720px; }
.mesba-access__empty { padding: 34px; text-align: center; }
.mesba-access__empty h3 { margin: 12px 0 4px; }
.mesba-access__empty p { color: #64746e; margin: 0 auto 18px; max-width: 520px; }
.mesba-access__list { display: grid; gap: 12px; }
.mesba-access__key { padding: 18px; }
.scope-list { display: flex; flex-wrap: wrap; gap: 6px; }
.key-actions { display: flex; }
.dialog-heading small { color: #718078; display: block; font-size: 13px; font-weight: 400; margin-top: 4px; }
.permission-heading { color: #213e35; font-size: 16px; font-weight: 700; }
.permission-subtitle { color: #718078; font-size: 13px; }
.permission-grid { border: 1px solid #e1e8e4; border-radius: 10px; overflow: hidden; }
.permission-row { display: flex; align-items: center; justify-content: space-between; min-height: 66px; padding: 10px 16px; }
.permission-row + .permission-row { border-top: 1px solid #edf1ef; }
.permission-row small { color: #718078; display: block; margin-top: 2px; }
.permission-row--sensitive { background: #fffdfa; }
.token-field >>> textarea { font-family: monospace; font-size: 14px; word-break: break-all; }
@media (max-width: 700px) {
  .mesba-access__header, .mesba-access__key > div { flex-direction: column; }
  .key-actions { margin-left: 58px; }
}
</style>
